import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { PATHS } from "../../../../constants/pathConstants";
import { FORM_IDS } from "../../../../constants/v3/formConstants";
import {
  selectHasAcceptedCurrentHipaaAuthorization,
  selectHasAcceptedCurrentPrivacyPolicy,
  selectHasAcceptedCurrentTermsAndConditions,
  setFormSubmissions
} from "../../../../store/reducers/formTemplates/FormTemplatesSlice";
import { openGLSnackbar } from "../../../../store/reducers/snackbar/openGLSnackbarSlice";
import PageTitle from "../../../../utils/PageTitle";
import GradientFramedLayout from "../../../v3/layouts/GradientFramedLayout";
import FormTemplateWrapper, {
  FormData
} from "../../../v3/sharedComponents/forms/FormTemplateWrapper";
import LoadingAnimation from "../../../v3/sharedComponents/LoadingAnimation";
import AgreementsUpdatedModal from "../../sharedComponents/modals/AgreementsUpdatedModal";
import {
  postFormSubmission,
  getFormSubmissions
} from "../../../../apis/v3/formTemplatesApi";
import GL_Logo from "../../../../assets/images/GL-Logo.svg";

interface FormToComplete {
  formId: string;
}

const UpdateAgreementsPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const privacyComplete = useSelector(selectHasAcceptedCurrentPrivacyPolicy);
  const termsComplete = useSelector(selectHasAcceptedCurrentTermsAndConditions);
  const hipaaComplete = useSelector(selectHasAcceptedCurrentHipaaAuthorization);

  const formsToComplete = useMemo<FormToComplete[]>(() => {
    const completion: Record<string, boolean> = {
      [FORM_IDS.PRIVACY_POLICY]: privacyComplete,
      [FORM_IDS.TERMS_AND_CONDITIONS]: termsComplete,
      [FORM_IDS.HIPAA_AUTHORIZATION]: hipaaComplete
    };
    return Object.values(FORM_IDS)
      .filter((formId) => !completion[formId])
      .map((formId) => ({ formId }));
  }, [privacyComplete, termsComplete, hipaaComplete]);

  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [collectedFormData, setCollectedFormData] = useState<FormData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAgreementsModal, setShowAgreementsModal] = useState(true);

  PageTitle("Update Agreements");

  // Scroll to top when form changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentFormIndex]);

  const currentForm = formsToComplete[currentFormIndex];
  const currentFormId = currentForm?.formId;
  const isLastForm = currentFormIndex === formsToComplete.length - 1;

  const handleFormComplete = async (formData: FormData) => {
    const updatedCollectedData = [...collectedFormData, formData];
    setCollectedFormData(updatedCollectedData);

    if (isLastForm) {
      setIsSubmitting(true);

      try {
        for (const data of updatedCollectedData) {
          await postFormSubmission({
            formId: data.formId,
            formAnswers: data.formAnswers
          });
        }

        const response = await getFormSubmissions();
        dispatch(setFormSubmissions((response as any).data));
        history.push(PATHS.LANDING_PAGE);
      } catch (error) {
        console.error("Error submitting forms:", error);
        setIsSubmitting(false);

        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "Error submitting forms",
            subText:
              "Sorry, something went wrong when submitting your forms. Please try again."
          })
        );
      }
    } else {
      setCurrentFormIndex(currentFormIndex + 1);
    }
  };

  if (formsToComplete.length === 0) {
    history.push(PATHS.LANDING_PAGE);
  }

  if (!currentFormId) {
    return null;
  }

  if (isSubmitting) {
    return (
      <GradientFramedLayout showHeader={false}>
        <Grid container sx={{ height: "90vh", position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2
            }}
          >
            <LoadingAnimation />
            <Typography variant="body1">Saving your agreements...</Typography>
          </Box>
        </Grid>
      </GradientFramedLayout>
    );
  }

  return (
    <GradientFramedLayout showHeader={false}>
      <AgreementsUpdatedModal
        isOpen={showAgreementsModal}
        onClose={() => setShowAgreementsModal(false)}
      />
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: {
            xs: "30px 16px 60px 16px",
            md: "64px 50px 50px 50px"
          },
          width: "100%",
          maxWidth: "900px"
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 4
          }}
        >
          <img
            src={GL_Logo}
            alt="Genomic Life Logo"
            style={{ width: "185px" }}
          />
        </Box>

        {/* Form Container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            backgroundColor: "#F4F6F9",
            borderRadius: "30px"
          }}
        >
          <Box sx={{ width: "100%" }}>
            <FormTemplateWrapper
              key={currentFormId}
              formId={currentFormId}
              deferSubmission={true}
              onCompleteWithData={handleFormComplete}
            />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default UpdateAgreementsPage;
